/**
 * MarketingDashboardPage — Phase 32.9 entry point.
 *
 * Summary tiles, a prominent "Needs Review" call-to-action when campaigns are
 * awaiting founder approval, and a recent-activity feed. Polls every 30 s.
 */

import { Navigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';

// ── Types ────────────────────────────────────────────────────────────────────

interface CampaignSummary {
  id: string;
  product_id: string;
  status: string;
  omega_log_seq: number;
  created_at: number;
  executed_at: number | null;
  slug: string | null;
  title_tag: string | null;
}

interface ListCampaignsResponse {
  campaigns: CampaignSummary[];
  total: number;
}

// ── Data ─────────────────────────────────────────────────────────────────────

function fetchCampaigns(): Promise<ListCampaignsResponse> {
  return apiFetch<ListCampaignsResponse>('/admin/marketing/campaigns');
}

function useCampaigns() {
  return useQuery({
    queryKey: ['marketing', 'campaigns'],
    queryFn: fetchCampaigns,
    refetchInterval: 30_000,
  });
}

// S53 — marketing overview (campaigns rollup + conversion funnel).
interface FunnelStageWire {
  stage: string;
  count: number;
  conversion_bps: number;
}
interface MarketingOverview {
  total_campaigns: number;
  active_campaigns: number;
  funnel: {
    stages: FunnelStageWire[];
    cost_cents: number;
    revenue_cents: number;
    cost_per_opportunity_cents: number;
    overall_conversion_bps: number;
  } | null;
}

function useOverview() {
  return useQuery({
    queryKey: ['marketing', 'overview'],
    queryFn: () => apiFetch<MarketingOverview>('/admin/marketing/overview'),
    refetchInterval: 30_000,
  });
}

const STAGE_LABELS: Record<string, string> = {
  visitor: 'Visitors',
  lead: 'Leads',
  mql: 'MQL',
  sql: 'SQL',
  customer: 'Customers',
};

function usdCents(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Conversion funnel + spend/ROI rollup (S53 item 12). */
function ConversionFunnel() {
  const q = useOverview();
  const f = q.data?.funnel ?? null;
  return (
    <section style={{ marginBottom: 24 }}>
      <div style={{ fontSize: '9pt', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--omega-ink)', marginBottom: 8 }}>
        Conversion Funnel
      </div>
      {q.isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {q.isError && <div style={{ color: '#f87171', fontSize: '9pt' }}>Failed to load funnel.</div>}
      {!q.isPending && !q.isError && (!f || f.stages.every((s) => s.count === 0)) && (
        <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic' }}>
          No funnel activity yet — events populate as campaigns drive visitors → leads → customers.
        </div>
      )}
      {f && f.stages.some((s) => s.count > 0) && (
        <>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
            {f.stages.map((s) => (
              <div key={s.stage} style={{ flex: '1 1 90px', minWidth: 90, background: 'rgba(255,255,255,0.05)', border: '1px solid var(--omega-rim, rgba(255,255,255,0.1))', borderRadius: 6, padding: '10px 12px' }}>
                <div style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{STAGE_LABELS[s.stage] ?? s.stage}</div>
                <div style={{ fontSize: '15pt', fontWeight: 700, color: 'var(--omega-ink)', fontVariantNumeric: 'tabular-nums' }}>{s.count.toLocaleString()}</div>
                <div style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)' }}>{(s.conversion_bps / 100).toFixed(1)}%</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>
            <span>Overall: <strong style={{ color: 'var(--omega-ink)' }}>{(f.overall_conversion_bps / 100).toFixed(2)}%</strong></span>
            <span>Spend: <strong style={{ color: 'var(--omega-ink)' }}>{usdCents(f.cost_cents)}</strong></span>
            <span>Revenue: <strong style={{ color: 'var(--omega-ink)' }}>{usdCents(f.revenue_cents)}</strong></span>
            <span>Cost/opportunity: <strong style={{ color: 'var(--omega-ink)' }}>{usdCents(f.cost_per_opportunity_cents)}</strong></span>
          </div>
        </>
      )}
    </section>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  pending:   '#7dd3fc',
  running:   '#a78bfa',
  approved:  '#6ee7b7',
  published: '#34d399',
  rejected:  '#f87171',
  failed:    '#f87171',
};

function statusColor(s: string): string {
  return STATUS_COLORS[s] ?? 'var(--omega-ink-subtle)';
}

function fmtTime(unix: number): string {
  return new Date(unix * 1000).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ label, value, color, highlight }: {
  label: string;
  value: string | number;
  color?: string;
  highlight?: boolean;
}) {
  return (
    <div
      style={{
        background: highlight
          ? 'rgba(110,231,183,0.08)'
          : 'var(--omega-surface, rgba(255,255,255,0.04))',
        border: highlight
          ? '1px solid rgba(110,231,183,0.4)'
          : '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
        borderRadius: 6,
        padding: '14px 18px',
        minWidth: 120,
      }}
    >
      <div style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: '18pt', fontWeight: 700, color: color ?? 'var(--omega-ink)' }}>
        {value}
      </div>
    </div>
  );
}

function NeedsReviewBanner({ campaigns }: { campaigns: CampaignSummary[] }) {
  if (campaigns.length === 0) return null;
  return (
    <div
      style={{
        background: 'rgba(110,231,183,0.07)',
        border: '1px solid rgba(110,231,183,0.35)',
        borderRadius: 6,
        padding: '14px 18px',
        marginBottom: 24,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span
          style={{
            display: 'inline-block',
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#6ee7b7',
            boxShadow: '0 0 6px 2px rgba(110,231,183,0.5)',
          }}
        />
        <span style={{ fontSize: '9.5pt', fontWeight: 700, color: '#6ee7b7', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          {campaigns.length} Campaign{campaigns.length !== 1 ? 's' : ''} Awaiting Your Review
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {campaigns.map(c => (
          <Link
            key={c.id}
            to={`/omega/marketing/campaigns/${c.id}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '7px 10px',
              background: 'rgba(110,231,183,0.06)',
              border: '1px solid rgba(110,231,183,0.18)',
              borderRadius: 4,
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <span style={{ flex: 1, fontSize: '9.5pt', color: 'var(--omega-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {c.title_tag ?? c.product_id}
            </span>
            {c.executed_at && (
              <span style={{ flexShrink: 0, fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>
                Ready {fmtTime(c.executed_at)}
              </span>
            )}
            <span style={{ flexShrink: 0, fontSize: '8.5pt', fontWeight: 700, color: '#6ee7b7', letterSpacing: '0.04em' }}>
              Review →
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function RecentRow({ c }: { c: CampaignSummary }) {
  return (
    <Link
      to={`/omega/marketing/campaigns/${c.id}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 0',
        borderTop: '1px solid var(--omega-rim, rgba(255,255,255,0.06))',
        textDecoration: 'none',
        color: 'inherit',
      }}
    >
      <span
        style={{
          flexShrink: 0,
          fontSize: '7.5pt',
          padding: '1px 6px',
          border: `1px solid ${statusColor(c.status)}`,
          color: statusColor(c.status),
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          fontWeight: 700,
          borderRadius: 3,
        }}
      >
        {c.status}
      </span>
      <span style={{ flex: 1, fontSize: '9.5pt', color: 'var(--omega-ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {c.title_tag ?? c.product_id}
      </span>
      <span style={{ flexShrink: 0, fontSize: '8.5pt', color: 'var(--omega-ink-subtle)' }}>
        {fmtTime(c.created_at)}
      </span>
    </Link>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function MarketingDashboardPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const { data, isPending, isError } = useCampaigns();

  const campaigns = data?.campaigns ?? [];
  const counts: Record<string, number> = {};
  for (const c of campaigns) counts[c.status] = (counts[c.status] ?? 0) + 1;

  // Campaigns the brain completed — awaiting founder approve/reject.
  const needsReview = campaigns.filter(c => c.status === 'approved' && c.executed_at !== null);

  const recent = campaigns.slice(0, 8);

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-mono)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs
        crumbs={[{ label: 'OMEGA', to: '/omega' }, { label: 'Marketing' }]}
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8 }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Marketing Brain
        </h1>
        {needsReview.length > 0 && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 20,
              height: 20,
              padding: '0 6px',
              borderRadius: 10,
              background: '#6ee7b7',
              color: '#0a0d12',
              fontSize: '8pt',
              fontWeight: 700,
            }}
          >
            {needsReview.length}
          </span>
        )}
        <Link
          to="/omega/marketing/campaigns"
          style={{
            marginLeft: 'auto',
            background: 'rgba(125,211,252,0.12)',
            border: '1px solid rgba(125,211,252,0.35)',
            borderRadius: 4,
            color: '#7dd3fc',
            fontFamily: 'var(--omega-mono)',
            fontSize: '8.5pt',
            fontWeight: 600,
            padding: '5px 12px',
            textDecoration: 'none',
            letterSpacing: '0.04em',
          }}
        >
          All Campaigns
        </Link>
      </div>

      {/* Pending review banner */}
      {!isPending && !isError && <NeedsReviewBanner campaigns={needsReview} />}

      {/* Stat cards */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 24 }}>
        <StatCard label="Total" value={data?.total ?? '—'} />
        <StatCard label="Needs Review" value={needsReview.length} color="#6ee7b7" highlight={needsReview.length > 0} />
        <StatCard label="Running" value={counts.running ?? 0} color="#a78bfa" />
        <StatCard label="Pending" value={counts.pending ?? 0} color="#7dd3fc" />
        <StatCard label="Rejected" value={counts.rejected ?? 0} color="#f87171" />
      </div>

      {/* Nav strip */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 24 }}>
        {[
          { to: '/omega/marketing/campaigns', label: 'Campaigns' },
          { to: '/omega/marketing/schedule',  label: 'Schedule' },
          { to: '/omega/marketing/metrics',   label: 'Metrics' },
        ].map(({ to, label }) => (
          <Link
            key={to}
            to={to}
            style={{
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid var(--omega-rim, rgba(255,255,255,0.1))',
              borderRadius: 4,
              color: 'var(--omega-ink)',
              fontFamily: 'var(--omega-mono)',
              fontSize: '8.5pt',
              fontWeight: 600,
              padding: '6px 14px',
              textDecoration: 'none',
              letterSpacing: '0.04em',
            }}
          >
            {label}
          </Link>
        ))}
      </div>

      {/* S53 — conversion funnel + spend/ROI rollup */}
      <ConversionFunnel />

      {/* Recent campaigns */}
      <section>
        <div style={{ fontSize: '9pt', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--omega-ink)', marginBottom: 4 }}>
          Recent Campaigns
        </div>
        {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
        {isError && <div style={{ color: '#f87171', fontSize: '9pt' }}>Failed to load campaigns.</div>}
        {!isPending && !isError && recent.length === 0 && (
          <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt', fontStyle: 'italic' }}>
            No campaigns yet — the brain will populate this when a product is published.
          </div>
        )}
        {recent.map(c => <RecentRow key={c.id} c={c} />)}
      </section>
    </div>
  );
}
